import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

// Types
export interface ForecastVersion {
  id: string;
  forecastId: string;
  versionNumber: number;
  name: string;
  modelType: string;
  modelVersion: string;
  modelParameters?: any;
  status: string;
  confidence: number;
  accuracyScore?: number;
  mape?: number;
  mae?: number;
  createdBy?: string;
  createdAt: string;
  changeReason?: string;
  changeType: string;
  snapshot: any;
  dataPointSnapshot: any[];
  changes?: {
    forecast: Record<string, { old: any; new: any }>;
    dataPoints: {
      added: number;
      removed: number;
      modified: number;
      totalOld: number;
      totalNew: number;
    };
  };
}

export interface ForecastVersionComparison {
  version1: {
    versionNumber: number;
    createdAt: string;
    createdBy?: string;
    changeType: string;
    modelType: string;
    modelParameters?: any;
    accuracyScore?: number;
  };
  version2: {
    versionNumber: number;
    createdAt: string;
    createdBy?: string;
    changeType: string;
    modelType: string;
    modelParameters?: any;
    accuracyScore?: number;
  };
  changes: {
    forecast: Record<string, { old: any; new: any }>;
    dataPoints: {
      added: number;
      removed: number;
      modified: number;
      totalOld: number;
      totalNew: number;
    };
  };
}

// API Service
const forecastVersionsApi = {
  getVersions: async (forecastId: string): Promise<ForecastVersion[]> => {
    const response = await api.get(`/forecasts/${forecastId}/versions`);
    return response.data;
  },

  getVersion: async (forecastId: string, versionNumber: number): Promise<ForecastVersion> => {
    const response = await api.get(`/forecasts/${forecastId}/versions/${versionNumber}`);
    return response.data;
  },

  createVersion: async (forecastId: string, data: {
    changeType: string;
    changeReason?: string;
    createdBy?: string;
  }): Promise<ForecastVersion> => {
    const response = await api.post(`/forecasts/${forecastId}/versions`, data);
    return response.data;
  },

  compareVersions: async (
    forecastId: string,
    version1: number,
    version2: number,
  ): Promise<ForecastVersionComparison> => {
    const response = await api.get(
      `/forecasts/${forecastId}/versions/compare/${version1}/${version2}`,
    );
    return response.data;
  },

  restoreVersion: async (
    forecastId: string,
    versionNumber: number,
    restoredBy?: string,
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(
      `/forecasts/${forecastId}/versions/${versionNumber}/restore`,
      { restoredBy },
    );
    return response.data;
  },
};

// React Query Hooks

/**
 * Get all versions for a forecast
 */
export function useForecastVersions(forecastId?: string) {
  return useQuery({
    queryKey: ['forecast-versions', forecastId],
    queryFn: () => forecastVersionsApi.getVersions(forecastId!),
    enabled: !!forecastId,
  });
}

/**
 * Get a specific version
 */
export function useForecastVersion(forecastId?: string, versionNumber?: number) {
  return useQuery({
    queryKey: ['forecast-versions', forecastId, versionNumber],
    queryFn: () => forecastVersionsApi.getVersion(forecastId!, versionNumber!),
    enabled: !!forecastId && !!versionNumber,
  });
}

/**
 * Create a new version
 */
export function useCreateForecastVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      forecastId,
      data,
    }: {
      forecastId: string;
      data: {
        changeType: string;
        changeReason?: string;
        createdBy?: string;
      };
    }) => forecastVersionsApi.createVersion(forecastId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['forecast-versions', variables.forecastId] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.forecastId] });
    },
  });
}

/**
 * Compare two versions
 */
export function useCompareForecastVersions(
  forecastId?: string,
  version1?: number,
  version2?: number,
) {
  return useQuery({
    queryKey: ['forecast-versions-compare', forecastId, version1, version2],
    queryFn: () => forecastVersionsApi.compareVersions(forecastId!, version1!, version2!),
    enabled: !!forecastId && !!version1 && !!version2,
  });
}

/**
 * Restore a version
 */
export function useRestoreForecastVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      forecastId,
      versionNumber,
      restoredBy,
    }: {
      forecastId: string;
      versionNumber: number;
      restoredBy?: string;
    }) => forecastVersionsApi.restoreVersion(forecastId, versionNumber, restoredBy),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['forecast-versions', variables.forecastId] });
      queryClient.invalidateQueries({ queryKey: ['forecasts', variables.forecastId] });
    },
  });
}
